/**
 * FractionStripSVG — Phase 1, Component 7 of the TeachBud Representation Catalog
 *
 * Renders a fraction strip (bar model / tape diagram) per §5.1 Fractions:
 *   - Horizontal rectangle divided into `denominator` equal segments
 *   - `numerator` segments are shaded blue; rest are white with gray border
 *   - Labels below each segment show the unit fraction
 *   - Student mode: blank bar with tick-mark guides — student shades the fraction
 *   - Fraction notation (e.g., "3/4") shown below the strip
 *   - Grades 3–5
 */

import React from 'react';

export interface FractionStripProps {
  numerator?: number;
  denominator?: number;
  /** When true, no segments are shaded — student fills in. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  /** Show the fraction notation label below. Default true. */
  showLabel?: boolean;
  /** Show unit fraction labels in each segment. Default true for denom ≤ 8. */
  showUnitLabels?: boolean;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W  = 560;
const BAR_X = 20;
const BAR_Y = 20;
const BAR_H = 64;
const BAR_W = VB_W - 40;
const LABEL_AREA_H = 40;
const VB_H  = BAR_Y + BAR_H + LABEL_AREA_H;

// Colors (spec §5.1 Fractions)
const SHADED_FILL   = '#3B82F6';  // blue-500
const SHADED_STROKE = '#1D4ED8';  // blue-700
const EMPTY_FILL    = '#FFFFFF';
const EMPTY_STROKE  = '#6B7280';  // gray-500
const LABEL_COLOR   = '#1E3A5F';
const NOTATION_COLOR= '#374151';

export function FractionStripSVG({
  numerator = 3,
  denominator = 4,
  studentMode = false,
  width,
  showLabel = true,
  showUnitLabels,
  className = '',
}: FractionStripProps) {
  const denom = Math.max(1, Math.min(12, denominator));
  const numer = studentMode ? 0 : Math.max(0, Math.min(denom, numerator));
  const showUnits = showUnitLabels ?? (denom <= 8);

  const segW = BAR_W / denom;

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  const UNIT_FS = denom <= 4 ? 14 : denom <= 6 ? 11 : 9;
  const NOTATION_FS = 18;

  // Unit fraction string: "1/4"
  function unitLabel(i: number): string {
    if (denom === 1) return '1';
    return `1/${denom}`;
  }

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW}
      height={svgH}
      className={className}
      role="img"
      aria-label={
        studentMode
          ? `Fraction strip for 1/${denom} — student shades`
          : `Fraction strip showing ${numer}/${denom}`
      }
    >
      <title>{studentMode ? `Fraction strip (student)` : `Fraction strip: ${numer}/${denom}`}</title>

      {/* Outer border of entire strip */}
      <rect
        x={BAR_X} y={BAR_Y}
        width={BAR_W} height={BAR_H}
        rx={6} ry={6}
        fill="none"
        stroke={EMPTY_STROKE}
        strokeWidth={2}
      />

      {/* Segments */}
      {Array.from({ length: denom }).map((_, i) => {
        const x = BAR_X + i * segW;
        const shaded = !studentMode && i < numer;
        // Clip rounded corners only on first and last segments
        const isFirst = i === 0;
        const isLast  = i === denom - 1;
        const rx = isFirst || isLast ? 6 : 0;
        const clipX = isFirst ? x : x - 0.5;
        const clipW = isFirst || isLast ? segW : segW + 0.5;

        return (
          <g key={i}>
            <rect
              x={clipX} y={BAR_Y}
              width={clipW} height={BAR_H}
              rx={rx} ry={rx}
              fill={shaded ? SHADED_FILL : EMPTY_FILL}
              stroke="none"
            />
            {/* Divider line (not on last) */}
            {i > 0 && (
              <line
                x1={x} y1={BAR_Y}
                x2={x} y2={BAR_Y + BAR_H}
                stroke={shaded ? SHADED_STROKE : EMPTY_STROKE}
                strokeWidth={1.5}
              />
            )}
            {/* Unit fraction label inside segment */}
            {showUnits && (
              <text
                x={x + segW / 2}
                y={BAR_Y + BAR_H / 2 + 5}
                textAnchor="middle"
                fontSize={UNIT_FS}
                fontFamily="Nunito, sans-serif"
                fontWeight={600}
                fill={shaded ? '#FFFFFF' : '#9CA3AF'}
                opacity={studentMode ? 0.4 : 0.8}
              >
                {unitLabel(i)}
              </text>
            )}
          </g>
        );
      })}

      {/* Outer border drawn on top of fills */}
      <rect
        x={BAR_X} y={BAR_Y}
        width={BAR_W} height={BAR_H}
        rx={6} ry={6}
        fill="none"
        stroke={EMPTY_STROKE}
        strokeWidth={2}
      />

      {/* Student-mode shading prompt arrows */}
      {studentMode && (
        <>
          <text
            x={BAR_X + BAR_W / 2} y={BAR_Y + BAR_H / 2 + 6}
            textAnchor="middle"
            fontSize={12}
            fontFamily="Inter, sans-serif"
            fontWeight={500}
            fill="#9CA3AF"
          >
            Shade the fraction
          </text>
        </>
      )}

      {/* Fraction notation below */}
      {showLabel && (
        <text
          x={BAR_X + BAR_W / 2}
          y={BAR_Y + BAR_H + LABEL_AREA_H - 6}
          textAnchor="middle"
          fontSize={NOTATION_FS}
          fontFamily="Nunito, sans-serif"
          fontWeight={700}
          fill={NOTATION_COLOR}
        >
          {studentMode ? `___  /  ${denom}` : `${numer} / ${denom}`}
        </text>
      )}
    </svg>
  );
}

// ── Hint parser ───────────────────────────────────────────────────────────────
export function parseFractionStripHint(hint: string): FractionStripProps {
  const h = hint.toLowerCase();

  // "fraction strip: 3/4 shaded" or "shade 3/4" or "3 out of 4"
  const fracMatch = h.match(/(\d+)\s*[\/÷]\s*(\d+)/);
  if (fracMatch) {
    return {
      numerator: parseInt(fracMatch[1]),
      denominator: parseInt(fracMatch[2]),
      studentMode: /student|blank|shade|fill|empty/i.test(h),
    };
  }

  // "compare 2/3 and 3/4" — show student strip for larger denom
  const multiMatch = [...h.matchAll(/(\d+)\s*\/\s*(\d+)/g)];
  if (multiMatch.length >= 2) {
    return {
      numerator: parseInt(multiMatch[0][1]),
      denominator: parseInt(multiMatch[0][2]),
      studentMode: true,
    };
  }

  // Just a denominator hint: "fourths" / "thirds"
  const denomWords: Record<string, number> = { half: 2, halves: 2, third: 3, thirds: 3, fourth: 4, fourths: 4, fifth: 5, fifths: 5, sixth: 6, sixths: 6, eighth: 8, eighths: 8 };
  for (const [word, d] of Object.entries(denomWords)) {
    if (h.includes(word)) return { numerator: 0, denominator: d, studentMode: true };
  }

  return { numerator: 3, denominator: 4, studentMode: true };
}
